import { Injectable } from '@angular/core';
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {Observable, throwError} from "rxjs";
import {environment} from "../../environments/environment";
import {catchError, map, switchMap, tap} from "rxjs/operators";
import {AuthService} from "./auth.service";

@Injectable({
  providedIn: 'root'
})
export class UserProfileService {

  private apiUrl = environment.api.url;

  token: string;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
  ) { }

  /**
   * Submits user profile data to the backend API.
   * @param profileData The data from the user profile form.
   * @returns Observable<{data: any, message: string}> that includes a response message and data.
   */
  submitUserProfile(profileData: any): Observable<{data: any, message: string}> {
    // const url = `${this.apiUrl}/user-profiles`;
    const url = `${this.apiUrl}/user/update-profile`;

    this.authService.token().subscribe(token => {
      this.token = token;
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}` // Use the token for authorization
      })
    };

    return this.http.post<any>(url, profileData, httpOptions).pipe(
      // tap(response => console.log('[user-profile.service.ts] response', response)),
      map(response => {
        if (!response.data) {
          // If the response object does not have a data property, throw an error
          throw new Error('User profile submission failed: ' + response.message);
        }
        console.log('User profile submission response:', response);
        // Return the response part that is needed
        return {
          data: response.data,
          message: response.message
        };
      }),
      catchError(error => {
        // Log or handle error appropriately within the service
        console.error('Error submitting user profile:', error);
        return throwError(error); // Re-throw the error for further handling upstream
      })
    );
  }

  /**
   * Retrieves user profile data from the backend API.
   * @returns Observable<{data: any, message: string}> that includes a response message and data.
   */
  getUserProfile(): Observable<{data: any, message: string}> {
    // const url = `${this.apiUrl}/user-profiles`;
    const url = `${this.apiUrl}/user/get-profile`;

    this.authService.token().subscribe(token => {
      this.token = token;
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}` // Use the token for authorization
      })
    };

    const data = {}

    return this.http.post<any>(url, data, httpOptions).pipe(
      // tap(response => console.log('[user-profile.service.ts] response', response)),
      map(response => {
        if (!response.data) {
          // If the response object does not have a data property, throw an error
          throw new Error('Failed to retrieve user profile: ' + response.message);
        }
        // console.log('User profile retrieval response:', response);
        return {
          data: response.data,
          message: response.message
        };
      }),
      catchError(error => {
        console.error('Error retrieving user profile:', error);
        return throwError(error); // Re-throw the error for further handling upstream
      })
    );
  }

  /**
   * Updates user profile data on the backend API.
   * @param id The id of the user profile.
   * @param profileData The data from the user profile form.
   * @returns Observable<{data: any, message: string}> that includes a response message and data.
   */
  updateUserProfile(id: number, profileData: any): Observable<{data: any, message: string}> {
    const url = `${this.apiUrl}/user-profiles/${id}`;

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}` // Use the token for authorization
      })
    };

    return this.http.put<any>(url, profileData, httpOptions).pipe(
      // tap(response => console.log('[user-profile.service.ts] response', response)),
      map(response => {
        if (!response.data) {
          // If the response object does not have a data property, throw an error
          throw new Error('User profile update failed: ' + response.message);
        }
        console.log('User profile update response:', response);
        // Return the response part that is needed
        return {
          data: response.data,
          message: response.message
        };
      }),
      catchError(error => {
        // Log or handle error appropriately within the service
        console.error('Error updating user profile:', error);
        return throwError(error); // Re-throw the error for further handling upstream
      })
    );
  }

  /**
   * Skips the user setup process on the backend API.
   * @returns Observable<{message: string}> that includes a response message.
   */
  skipSetup(): Observable<{message: string}> {
    const url = `${this.apiUrl}/user-profiles/skip-setup`;

    // Fetch the token and then make the HTTP request
    return this.authService.token().pipe(
      tap(token => {
        this.token = token;
      }),
      switchMap(() => {
        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.token}` // Use the token for authorization
          })
        };

        return this.http.post<any>(url, {}, httpOptions).pipe(
          // tap(response => console.log('[setup.service.ts] response', response)),
          map(response => {
            if (!response) {
              // If the response object is empty, throw an error
              throw new Error('Setup skip failed: No response from server');
            }
            console.log('Setup skip response:', response);
            // Return the response message
            return { message: response.metadata.message || 'Setup skipped successfully' };
          }),
          catchError(error => {
            // Log or handle error appropriately within the service
            console.error('Error during setup skip:', error);
            return throwError(() => new Error('Error during setup skip: ' + (error.error?.message || error.message)));
          })
        );
      })
    );
  }

}
